/**
 * User Detail Page
 * Displays detailed information for a selected user with action buttons
 */

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { usePageTitle } from "../contexts/PageTitleContext";
import { usersService } from "../services/api/users.service";
import { rolesService } from "../services/api/roles.service";
import { settingsService } from "../services/api/settings.service";
import { workflowService } from "../services/api/workflow.service";
import type { QueryWorkflowActionDto } from "../types/workflow.types";
import type { ApiUser, UpdateUserDto } from "../types/user.types";
import { UserStatusId } from "../types/user.types";
import type { Role } from "../types/role.types";
import BusyOverlay from "../components/common/BusyOverlay";
import WorkflowStages from "../components/common/WorkflowStages";
import History from "../components/common/History";
import QueryRemarks from "../components/common/QueryRemarks";
import QueryApplicationForm from "../components/common/QueryApplicationForm";
import { apiRequest } from "../api/apiClient";
import {
  ApplicationPermissionId,
  HasPermission,
  ModuleId,
} from "../utils/permissions";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import {
  showSuccessToast,
  showErrorToast,
  showDeleteConfirm,
  showConfirm,
} from "../utils/toast";
import { GENERIC_ERROR_MESSAGE } from "../utils/constants";
import { sanitizePhoneNumberInput, validatePhoneNumber } from "../utils/phone";
import { getFullPath } from "../utils/getFullPath";
import "../styles/pages/UserDetail.css";
import "../styles/pages/Users.css";

export const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setPageTitle } = usePageTitle();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState<ApiUser | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [isAtMyApprovalLevel, setIsAtMyApprovalLevel] = useState(false);
  const [currentStageOrder, setCurrentStageOrder] = useState(0);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState<UpdateUserDto>({});
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [editValidationErrors, setEditValidationErrors] = useState<
    Record<string, string>
  >({});
  const [editPhotoFile, setEditPhotoFile] = useState<File | null>(null);
  const [editPhotoPreview, setEditPhotoPreview] = useState<string | null>(null);
  const [maxDateOfBirth, setMaxDateOfBirth] = useState<string>("");

  // Signature upload modal state
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null);
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [signatureError, setSignatureError] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [showQueryRemarks, setShowQueryRemarks] = useState(false);
  const [hasQueryRemarks, setHasQueryRemarks] = useState(false);
  const [showQueryForm, setShowQueryForm] = useState(false);

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 881px)").matches;
    setSidebarOpen(isDesktop);

    const handleResize = () => {
      const isDesktopView = window.matchMedia("(min-width: 881px)").matches;
      setSidebarOpen(isDesktopView);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setPageTitle("User Detail", "View user information");
    fetchData();
    fetchMinimumAge();
  }, [id, setPageTitle]);

  const fetchData = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError("");
      const [userData, rolesData] = await Promise.all([
        usersService.getUserById(parseInt(id, 10)),
        rolesService.getRoles(),
      ]);
      setUser(userData);
      setRoles(Array.isArray(rolesData) ? rolesData : []);

      // Check if workflow is at user's approval level and get current stage
      if (userData.workflowId) {
        try {
          const [atMyLevel, currentStage] = await Promise.all([
            workflowService.isAtMyApprovalLevel(
              userData.workflowId,
              userData.id,
            ),
            workflowService.getCurrentStageRole(
              userData.workflowId,
              userData.id,
            ),
          ]);
          setIsAtMyApprovalLevel(atMyLevel);
          setCurrentStageOrder(currentStage.stageOrder);
        } catch (err) {
          console.error("Error checking approval level:", err);
          setIsAtMyApprovalLevel(false);
          setCurrentStageOrder(0);
        }
      } else {
        setIsAtMyApprovalLevel(false);
        setCurrentStageOrder(0);
      }

      try {
        const remarks = await workflowService.getRequeryRemarks(
          "Users",
          userData.id,
        );
        setHasQueryRemarks(remarks.length > 0);
      } catch {
        setHasQueryRemarks(false);
      }
    } catch (err: any) {
      console.error("Error fetching user:", err);
      setError(err?.message || "Failed to load user details");
    } finally {
      setLoading(false);
    }
  };

  const fetchMinimumAge = async () => {
    try {
      const setting = await settingsService.getSettingByKey("minimumAge");
      if (setting && setting.value) {
        const age = parseInt(setting.value, 10);
        const today = new Date();
        const maxDate = new Date(
          today.getFullYear() - age,
          today.getMonth(),
          today.getDate(),
        );
        setMaxDateOfBirth(maxDate.toISOString().split("T")[0]);
      }
    } catch (err) {
      console.error("Error fetching minimum age setting:", err);
      const today = new Date();
      const maxDate = new Date(
        today.getFullYear() - 18,
        today.getMonth(),
        today.getDate(),
      );
      setMaxDateOfBirth(maxDate.toISOString().split("T")[0]);
    }
  };

  const openEditModal = () => {
    if (!user) return;
    setEditFormData({
      uid: (user as any).uid,
      userNo: user.userNo,
      username: user.username,
      firstName: user.firstName,
      middleName: user.middleName,
      lastName: user.lastName,
      genderId: user.genderId,
      dateOfBirth: user.dateOfBirth?.split("T")[0],
      nationalIdNumber: user.nationalIdNumber,
      email: user.email,
      primaryPhone: user.primaryPhone,
      secondaryPhone: user.secondaryPhone,
      roleId: user.roleId,
      isActive: user.isActive,
      isConfirmed: user.isConfirmed,
      isSystemAdmin: user.isSystemAdmin,
      isClientAdmin: user.isClientAdmin,
      isUserOnly: user.isUserOnly,
      profileImagePath: user.profileImagePath,
    });
    if (user.profileImagePath) {
      setEditPhotoPreview(getFullPath(user.profileImagePath) ?? null);
    }
    setShowEditModal(true);
  };

  const resetEditForm = () => {
    setEditFormData({});
    setEditError("");
    setEditValidationErrors({});
    setEditPhotoFile(null);
    setEditPhotoPreview(null);
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setEditError("");
    if (!user) return;

    const errors: Record<string, string> = {};
    const primaryPhoneError = validatePhoneNumber(editFormData.primaryPhone, {
      required: true,
    });
    if (primaryPhoneError) {
      errors.primaryPhone = primaryPhoneError;
    }
    const secondaryPhoneError = validatePhoneNumber(
      editFormData.secondaryPhone,
    );
    if (secondaryPhoneError) {
      errors.secondaryPhone = secondaryPhoneError;
    }

    setEditValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      setEditLoading(true);
      const payload = {
        id: user.id,
        ...editFormData,
      };

      await usersService.updateUser(
        user.id,
        payload,
        editPhotoFile || undefined,
      );
      setShowEditModal(false);
      resetEditForm();
      await fetchData();
      showSuccessToast("User updated successfully.");
    } catch (err: any) {
      setEditError(err?.message || "Failed to update user");
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setEditError("Please select an image file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setEditError("Image size must be less than 5MB");
        return;
      }
      setEditPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeEditPhoto = () => {
    setEditPhotoFile(null);
    setEditPhotoPreview(null);
    setEditFormData({ ...editFormData, profileImagePath: "" });
  };

  const getRoleName = (roleId: number): string => {
    const role = roles.find((r) => r.id === roleId);
    return role?.name || `Role ${roleId}`;
  };

  const handleToggleStatus = async () => {
    if (!user) return;
    const action = user.isActive ? "deactivate" : "activate";
    const confirmed = await showConfirm(
      `${action.charAt(0).toUpperCase() + action.slice(1)} User`,
      `Are you sure you want to ${action} user "${user.username}"?`,
      "Yes",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setActionLoading(true);
      await usersService.toggleUserStatus(user.id, !user.isActive);
      await fetchData();
      showSuccessToast(`User has been ${action}d successfully.`);
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!user) return;
    const confirmed = await showDeleteConfirm(user.username, "user");
    if (!confirmed) return;

    try {
      setActionLoading(true);
      await usersService.deleteUser(user.id);
      showSuccessToast("User has been deleted successfully.");
      navigate("/admin/users");
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
      setActionLoading(false);
    }
  };

  const handleCreatePassword = async () => {
    if (!user) return;
    const confirmed = await showConfirm(
      "Send Password Creation Link",
      `Send a password creation link to ${user.email}?`,
      "Yes, send it",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setActionLoading(true);
      const emailConfigResponse = await apiRequest(
        "/Email/get-email-configuration",
        { method: "GET" },
      );
      if (!emailConfigResponse.ok) {
        throw new Error(
          "Email configuration not found. Please configure email settings first.",
        );
      }

      const response = await apiRequest(
        "/Password/send-create-password-email",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId: user.id,
            baseUrl: `${window.location.origin}/reset-password`,
          }),
        },
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(
          error.message || "Failed to send password creation link",
        );
      }

      showSuccessToast(`Password creation link has been sent to ${user.email}`);
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setActionLoading(false);
    }
  };

  const openSignatureModal = () => {
    setShowSignatureModal(true);
    setSignatureError("");
    if (user?.signaturePath) {
      setSignaturePreview(user.signaturePath);
    }
  };

  const resetSignatureForm = () => {
    setSignatureFile(null);
    setSignaturePreview(null);
    setSignatureError("");
  };

  const handleSignatureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setSignatureError("Please select an image file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setSignatureError("Image size must be less than 5MB");
        return;
      }
      setSignatureFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignaturePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeSignature = () => {
    setSignatureFile(null);
    setSignaturePreview(null);
  };

  const handleUploadSignature = async () => {
    if (!user || !signatureFile) {
      setSignatureError("Please select a signature image");
      return;
    }

    try {
      setSignatureLoading(true);
      setSignatureError("");

      await usersService.uploadSignature(user.id, signatureFile);
      setShowSignatureModal(false);
      resetSignatureForm();
      await fetchData();
      showSuccessToast("Signature uploaded successfully.");
    } catch (err: any) {
      setSignatureError(err?.message || "Failed to upload signature");
    } finally {
      setSignatureLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!user) return;
    const confirmed = await showConfirm(
      "Submit User",
      `Are you sure you want to submit user "${user.firstName} ${user.lastName}" for approval?`,
      "Yes, submit",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setActionLoading(true);
      await usersService.changeStatus(user.id, UserStatusId.Submitted);
      await fetchData();
      showSuccessToast("User has been submitted for approval.");
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!user || !user.workflowId) return;
    const confirmed = await showConfirm(
      "Approve User",
      `Are you sure you want to approve user "${user.firstName} ${user.lastName}"?`,
      "Yes, approve",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setActionLoading(true);

      // Get workflow details to check if this is the final stage
      const workflow = await workflowService.getWorkflowById(user.workflowId);
      const totalStages = workflow.stages?.length || 0;
      const isFinalStage = currentStageOrder >= totalStages;

      // Approve the workflow action
      await workflowService.approve({
        workflowId: user.workflowId,
        recordId: user.id,
        entityName: "Users",
        comments: "User approved",
      });

      // If this is the final approval stage, update status to Approved
      if (isFinalStage) {
        await usersService.changeStatus(user.id, UserStatusId.Approved);
      }

      await fetchData();
      showSuccessToast("User has been approved successfully.");
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setActionLoading(false);
    }
  };

  const handleQuery = () => {
    if (!user || !user.workflowId) return;
    setShowQueryForm(true);
  };

  const handleSubmitQuery = async (payload: QueryWorkflowActionDto) => {
    if (!user || !user.workflowId) return;
    try {
      setActionLoading(true);

      await workflowService.query(payload);

      // Change status to Queried to move the workflow back one step
      await usersService.changeStatus(user.id, UserStatusId.Queried);

      await fetchData();
      showSuccessToast("User has been queried successfully.");
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!user || !user.workflowId) return;

    // Import Swal for custom input dialog
    const Swal = (await import("sweetalert2")).default;

    const result = await Swal.fire({
      title: "Reject User",
      html: `
        <p>Please provide a reason for rejecting user "${user.firstName} ${user.lastName}":</p>
      `,
      input: "textarea",
      inputPlaceholder: "Enter your rejection reason here...",
      inputAttributes: {
        "aria-label": "Rejection reason",
        style: "min-height: 100px;",
      },
      showCancelButton: true,
      confirmButtonText: "Reject User",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      inputValidator: (value) => {
        if (!value || value.trim() === "") {
          return "You must provide a reason for rejection!";
        }
      },
    });

    if (!result.isConfirmed || !result.value || !result.value.trim()) return;

    const rejectComment = result.value.trim();

    try {
      setActionLoading(true);

      // Reject the workflow action with the provided comment
      await workflowService.reject({
        workflowId: user.workflowId,
        recordId: user.id,
        entityName: "Users",
        comments: rejectComment,
      });

      // Change status to Rejected
      await usersService.changeStatus(user.id, UserStatusId.Rejected);

      await fetchData();
      showSuccessToast("User has been rejected successfully.");
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-ZA", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  if (!HasPermission(ModuleId.Users, ApplicationPermissionId.View)) {
    return (
      <div
        className={`dashboard-body ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
      >
        <Topbar
          onMenuToggle={() => setSidebarOpen((prev) => !prev)}
          sidebarOpen={sidebarOpen}
        />
        <Sidebar isOpen={sidebarOpen} />
        <main className="main-content">
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            <span>You do not have permission to view users</span>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div
      className={`dashboard-body ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
    >
      <Topbar
        onMenuToggle={() => setSidebarOpen((prev) => !prev)}
        sidebarOpen={sidebarOpen}
      />
      <Sidebar isOpen={sidebarOpen} />
      <main className="main-content">
        <div className="user-detail-page">
          <BusyOverlay
            isVisible={loading || actionLoading}
            message={
              actionLoading ? "Processing..." : "Loading user details..."
            }
          />

          {error && (
            <div className="alert alert-error">
              <span className="alert-icon">⚠️</span>
              <span>{error}</span>
              <button onClick={() => setError("")} className="alert-close">
                ✕
              </button>
            </div>
          )}

          {user && (
            <div className="user-detail-layout">
              <button
                className="user-detail-back user-detail-back--mobile"
                onClick={() => navigate("/admin/users")}
              >
                ← Back to Users
              </button>
              {/* Column 1: User Details */}
              <div className="user-detail-info">
                {/* Profile header */}
                <div className="user-detail-profile-header">
                  <div className="user-detail-avatar">
                    {user.profileImagePath ? (
                      <img
                        src={getFullPath(user.profileImagePath) || undefined}
                        alt={`${user.firstName} ${user.lastName}`}
                      />
                    ) : (
                      <>
                        {user.firstName?.charAt(0)}
                        {user.lastName?.charAt(0)}
                      </>
                    )}
                  </div>
                  <div className="user-detail-profile-info">
                    <h2>
                      {user.firstName}{" "}
                      {user.middleName ? `${user.middleName} ` : ""}
                      {user.lastName}
                    </h2>
                    <div className="user-detail-profile-meta">
                      <span>@{user.username}</span>
                      <span className="divider">•</span>
                      <span>{getRoleName(user.roleId)}</span>
                      <span className="divider">•</span>
                      <span
                        className={`status-badge ${user.isActive ? "status-badge--active" : "status-badge--inactive"}`}
                      >
                        {user.status?.name ||
                          user.statusName ||
                          (user.isActive ? "Active" : "Inactive")}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Personal Information */}
                <h3 className="user-detail-section-title">
                  Personal Information
                </h3>
                <div className="user-detail-grid">
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">UID</div>
                    <div className="user-detail-field-value">
                      {(user as any).uid || user.userNo || user.id}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">Username</div>
                    <div className="user-detail-field-value">
                      {user.username}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">First Name</div>
                    <div className="user-detail-field-value">
                      {user.firstName}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">Middle Name</div>
                    <div
                      className={`user-detail-field-value ${!user.middleName ? "empty" : ""}`}
                    >
                      {user.middleName || "—"}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">Last Name</div>
                    <div className="user-detail-field-value">
                      {user.lastName}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">Gender</div>
                    <div className="user-detail-field-value">
                      {user.genderId === 1
                        ? "Male"
                        : user.genderId === 2
                          ? "Female"
                          : `Gender ${user.genderId}`}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">Date of Birth</div>
                    <div
                      className={`user-detail-field-value ${!user.dateOfBirth ? "empty" : ""}`}
                    >
                      {formatDate(user.dateOfBirth) || "—"}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">National ID</div>
                    <div
                      className={`user-detail-field-value ${!user.nationalIdNumber ? "empty" : ""}`}
                    >
                      {user.nationalIdNumber || "—"}
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <h3 className="user-detail-section-title">
                  Contact Information
                </h3>
                <div className="user-detail-grid">
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">Email</div>
                    <div className="user-detail-field-value">{user.email}</div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">Primary Phone</div>
                    <div
                      className={`user-detail-field-value ${!user.primaryPhone ? "empty" : ""}`}
                    >
                      {user.primaryPhone || "—"}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">
                      Secondary Phone
                    </div>
                    <div
                      className={`user-detail-field-value ${!user.secondaryPhone ? "empty" : ""}`}
                    >
                      {user.secondaryPhone || "—"}
                    </div>
                  </div>
                </div>

                {/* Role & Access */}
                <h3 className="user-detail-section-title">Role & Access</h3>
                <div className="user-detail-grid">
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">Role</div>
                    <div className="user-detail-field-value">
                      {getRoleName(user.roleId)}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">Status</div>
                    <div className="user-detail-field-value">
                      <span
                        className={`status-badge ${user.isActive ? "status-badge--active" : "status-badge--inactive"}`}
                      >
                        {user.status?.name ||
                          user.statusName ||
                          (user.isActive ? "Active" : "Inactive")}
                      </span>
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">Confirmed</div>
                    <div className="user-detail-field-value">
                      {user.isConfirmed ? "Yes" : "No"}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">System Admin</div>
                    <div className="user-detail-field-value">
                      {user.isSystemAdmin ? "Yes" : "No"}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">Client Admin</div>
                    <div className="user-detail-field-value">
                      {user.isClientAdmin ? "Yes" : "No"}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">User Only</div>
                    <div className="user-detail-field-value">
                      {user.isUserOnly ? "Yes" : "No"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Column 2: Action Buttons */}
              <div className="user-detail-actions">
                <button
                  className="user-detail-back user-detail-back--desktop"
                  onClick={() => navigate("/admin/users")}
                >
                  ← Back to Users
                </button>
                {HasPermission(ModuleId.Users, ApplicationPermissionId.Edit) &&
                  (user?.statusId === UserStatusId.Created ||
                    user?.statusId === UserStatusId.Queried) && (
                    <button
                      className="user-detail-action-btn user-detail-action-btn--success"
                      onClick={handleSubmit}
                      title="Submit user for approval"
                    >
                      <span className="action-icon">📤</span>
                      Submit
                    </button>
                  )}
                {HasPermission(
                  ModuleId.Users,
                  ApplicationPermissionId.Approve,
                ) &&
                  user?.workflowId &&
                  isAtMyApprovalLevel &&
                  (user?.statusId === UserStatusId.Submitted ||
                    user?.statusId === UserStatusId.InProgress) && (
                    <button
                      className="user-detail-action-btn user-detail-action-btn--success"
                      onClick={handleApprove}
                      title="Approve user"
                    >
                      <span className="action-icon">✅</span>
                      Approve
                    </button>
                  )}
                {HasPermission(
                  ModuleId.Users,
                  ApplicationPermissionId.Approve,
                ) &&
                  user?.workflowId &&
                  isAtMyApprovalLevel &&
                  (user?.statusId === UserStatusId.Submitted ||
                    user?.statusId === UserStatusId.InProgress) && (
                    <button
                      className="user-detail-action-btn user-detail-action-btn--warning"
                      onClick={handleQuery}
                      title="Query user"
                    >
                      <span className="action-icon">❓</span>
                      Query
                    </button>
                  )}
                {HasPermission(
                  ModuleId.Users,
                  ApplicationPermissionId.Approve,
                ) &&
                  user?.workflowId &&
                  isAtMyApprovalLevel &&
                  (user?.statusId === UserStatusId.Submitted ||
                    user?.statusId === UserStatusId.InProgress) && (
                    <button
                      className="user-detail-action-btn user-detail-action-btn--danger"
                      onClick={handleReject}
                      title="Reject user"
                    >
                      <span className="action-icon">❌</span>
                      Reject
                    </button>
                  )}
                {HasPermission(
                  ModuleId.Users,
                  ApplicationPermissionId.Edit,
                ) && (
                  <>
                    <button
                      className="user-detail-action-btn user-detail-action-btn--primary"
                      onClick={openEditModal}
                      title="Edit this user"
                    >
                      <span className="action-icon">✏️</span>
                      Edit
                    </button>
                    <button
                      className="user-detail-action-btn user-detail-action-btn--warning"
                      onClick={handleToggleStatus}
                    >
                      <span className="action-icon">
                        {user.isActive ? "🔒" : "🔓"}
                      </span>
                      {user.isActive ? "Deactivate" : "Activate"}
                    </button>
                    <button
                      className="user-detail-action-btn"
                      onClick={handleCreatePassword}
                    >
                      <span className="action-icon">🔑</span>
                      Create Password
                    </button>
                    <button
                      className="user-detail-action-btn user-detail-action-btn--primary"
                      onClick={openSignatureModal}
                      title={
                        user.signatureExists
                          ? "Update signature"
                          : "Add signature"
                      }
                      style={{ background: "#6f42c1", color: "#fff" }}
                    >
                      <span className="action-icon">✍️</span>
                      {user.signatureExists
                        ? "Update Signature"
                        : "Add Signature"}
                    </button>
                  </>
                )}
                {HasPermission(
                  ModuleId.Users,
                  ApplicationPermissionId.Delete,
                ) && (
                  <button
                    className="user-detail-action-btn user-detail-action-btn--danger"
                    onClick={handleDelete}
                  >
                    <span className="action-icon">🗑️</span>
                    Delete
                  </button>
                )}
                <button
                  className="user-detail-action-btn"
                  onClick={() => setShowHistory(true)}
                >
                  <span className="action-icon">🕘</span>
                  History
                </button>
                {hasQueryRemarks && (
                  <button
                    className="user-detail-action-btn user-detail-action-btn--warning"
                    onClick={() => setShowQueryRemarks(true)}
                  >
                    <span className="action-icon">💬</span>
                    Query Remarks
                  </button>
                )}

                {/* Workflow Progress */}
                {user?.workflowId &&
                  (user?.statusId === UserStatusId.Submitted ||
                    user?.statusId === UserStatusId.Queried ||
                    user?.statusId === UserStatusId.InProgress) && (
                    <div style={{ marginTop: "2rem" }}>
                      <WorkflowStages
                        workflowId={user.workflowId}
                        currentStageOrder={currentStageOrder}
                      />
                    </div>
                  )}
              </div>
            </div>
          )}

          <History
            entityName="Users"
            recordId={user?.id}
            isOpen={showHistory}
            onClose={() => setShowHistory(false)}
          />

          <QueryRemarks
            entityName="Users"
            recordId={user?.id}
            isOpen={showQueryRemarks}
            onClose={() => setShowQueryRemarks(false)}
          />

          <QueryApplicationForm
            isOpen={showQueryForm}
            onClose={() => setShowQueryForm(false)}
            entityName="Users"
            recordId={user?.id}
            workflowId={user?.workflowId}
            statusId={user?.statusId}
            onSubmit={handleSubmitQuery}
          />

          {/* Edit User Side Panel Modal */}
          {showEditModal && user && (
            <div className="modal-overlay modal-overlay--side-panel">
              <div className="modal-content modal-content--side-panel">
                <div className="modal-header">
                  <h2 className="modal-title">Edit User</h2>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={() => {
                      setShowEditModal(false);
                      resetEditForm();
                    }}
                    disabled={editLoading}
                  >
                    ✕
                  </button>
                </div>

                <form className="modal-form" onSubmit={handleUpdateUser}>
                  {editError && (
                    <div className="alert alert-error">
                      <span className="alert-icon">⚠️</span>
                      <span>{editError}</span>
                      <button
                        type="button"
                        onClick={() => setEditError("")}
                        className="alert-close"
                      >
                        ✕
                      </button>
                    </div>
                  )}

                  <div
                    className="form-group"
                    style={{ marginBottom: "var(--spacing-lg)" }}
                  >
                    <label className="form-label">Profile Photo</label>
                    <div className="photo-upload-container">
                      {editPhotoPreview ? (
                        <div className="photo-preview">
                          <img
                            src={editPhotoPreview || undefined}
                            alt="Preview"
                            className="preview-image"
                          />
                          <button
                            type="button"
                            className="btn btn--sm btn--danger"
                            onClick={removeEditPhoto}
                          >
                            Remove Photo
                          </button>
                        </div>
                      ) : editFormData.profileImagePath ? (
                        <div className="photo-preview">
                          <img
                            src={
                              getFullPath(editFormData.profileImagePath) ||
                              undefined
                            }
                            alt="Current"
                            className="preview-image"
                          />
                          <button
                            type="button"
                            className="btn btn--sm btn--danger"
                            onClick={removeEditPhoto}
                          >
                            Remove Photo
                          </button>
                        </div>
                      ) : (
                        <div className="photo-upload-area">
                          <input
                            type="file"
                            id="editPhotoUploadDetail"
                            accept="image/*"
                            onChange={handleEditPhotoChange}
                            style={{ display: "none" }}
                          />
                          <label
                            htmlFor="editPhotoUploadDetail"
                            className="photo-upload-label"
                          >
                            <div className="upload-icon">📷</div>
                            <div className="upload-text">
                              <span className="upload-title">
                                Click to upload photo
                              </span>
                              <span className="upload-subtitle">
                                PNG, JPG up to 5MB
                              </span>
                            </div>
                          </label>
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    className="form-group has-value"
                    style={{ marginBottom: "var(--spacing-md)" }}
                  >
                    <input
                      type="text"
                      className="form-input"
                      value={
                        (editFormData as any).uid || editFormData.userNo || ""
                      }
                      disabled
                      placeholder=" "
                      style={{
                        background: "#f1f1f1",
                        cursor: "not-allowed",
                      }}
                    />
                    <label className="form-label">UID</label>
                  </div>

                  <div className="form-grid-responsive">
                    <div
                      className={`form-group ${editFormData.firstName ? "has-value" : ""}`}
                    >
                      <input
                        type="text"
                        className="form-input"
                        value={editFormData.firstName || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            firstName: e.target.value,
                          })
                        }
                        placeholder=" "
                        required
                      />
                      <label className="form-label">
                        First Name <span className="required">*</span>
                      </label>
                    </div>

                    <div
                      className={`form-group ${editFormData.middleName ? "has-value" : ""}`}
                    >
                      <input
                        type="text"
                        className="form-input"
                        value={editFormData.middleName || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            middleName: e.target.value,
                          })
                        }
                        placeholder=" "
                      />
                      <label className="form-label">Middle Name</label>
                    </div>

                    <div
                      className={`form-group ${editFormData.lastName ? "has-value" : ""}`}
                    >
                      <input
                        type="text"
                        className="form-input"
                        value={editFormData.lastName || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            lastName: e.target.value,
                          })
                        }
                        placeholder=" "
                        required
                      />
                      <label className="form-label">
                        Last Name <span className="required">*</span>
                      </label>
                    </div>

                    <div className="form-group has-value">
                      <input
                        type="email"
                        className="form-input"
                        value={editFormData.email || ""}
                        placeholder=" "
                        disabled
                        style={{
                          background: "#f1f1f1",
                          cursor: "not-allowed",
                        }}
                      />
                      <label className="form-label">
                        Email <span className="required">*</span>
                      </label>
                    </div>

                    <div
                      className={`form-group ${editFormData.primaryPhone ? "has-value" : ""} ${editValidationErrors.primaryPhone ? "form-group--error" : ""}`}
                    >
                      <input
                        type="tel"
                        className={`form-input ${editValidationErrors.primaryPhone ? "form-input--error" : ""}`}
                        value={editFormData.primaryPhone || ""}
                        onChange={(e) => {
                          setEditFormData({
                            ...editFormData,
                            primaryPhone: sanitizePhoneNumberInput(
                              e.target.value,
                            ),
                          });
                          if (editValidationErrors.primaryPhone) {
                            setEditValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.primaryPhone;
                              return newErrors;
                            });
                          }
                        }}
                        placeholder=" "
                        required
                      />
                      <label className="form-label">
                        Primary Phone <span className="required">*</span>
                      </label>
                      {editValidationErrors.primaryPhone && (
                        <div className="field-error">
                          {editValidationErrors.primaryPhone}
                        </div>
                      )}
                    </div>

                    <div
                      className={`form-group ${editFormData.secondaryPhone ? "has-value" : ""} ${editValidationErrors.secondaryPhone ? "form-group--error" : ""}`}
                    >
                      <input
                        type="tel"
                        className={`form-input ${editValidationErrors.secondaryPhone ? "form-input--error" : ""}`}
                        value={editFormData.secondaryPhone || ""}
                        onChange={(e) => {
                          setEditFormData({
                            ...editFormData,
                            secondaryPhone: sanitizePhoneNumberInput(
                              e.target.value,
                            ),
                          });
                          if (editValidationErrors.secondaryPhone) {
                            setEditValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.secondaryPhone;
                              return newErrors;
                            });
                          }
                        }}
                        placeholder=" "
                      />
                      <label className="form-label">Secondary Phone</label>
                      {editValidationErrors.secondaryPhone && (
                        <div className="field-error">
                          {editValidationErrors.secondaryPhone}
                        </div>
                      )}
                    </div>

                    <div
                      className={`form-group ${editFormData.nationalIdNumber ? "has-value" : ""}`}
                    >
                      <input
                        type="text"
                        className="form-input"
                        value={editFormData.nationalIdNumber || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            nationalIdNumber: e.target.value,
                          })
                        }
                        placeholder=" "
                      />
                      <label className="form-label">National ID</label>
                    </div>

                    <div
                      className={`form-group ${editFormData.dateOfBirth ? "has-value" : ""}`}
                    >
                      <input
                        type="date"
                        className="form-input"
                        value={editFormData.dateOfBirth || ""}
                        max={maxDateOfBirth}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            dateOfBirth: e.target.value,
                          })
                        }
                        placeholder=" "
                        required
                      />
                      <label className="form-label">
                        Date of Birth <span className="required">*</span>
                      </label>
                    </div>

                    <div className="form-group has-value">
                      <select
                        className="form-input"
                        value={editFormData.genderId || 1}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            genderId: parseInt(e.target.value, 10),
                          })
                        }
                        required
                      >
                        <option value={1}>Male</option>
                        <option value={2}>Female</option>
                      </select>
                      <label className="form-label">
                        Gender <span className="required">*</span>
                      </label>
                    </div>

                    <div
                      className={`form-group ${editFormData.roleId ? "has-value" : ""}`}
                    >
                      <select
                        className="form-input"
                        value={editFormData.roleId || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            roleId: parseInt(e.target.value, 10),
                          })
                        }
                        required
                      >
                        <option value="">Select Role</option>
                        {roles.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.name}
                          </option>
                        ))}
                      </select>
                      <label className="form-label">
                        Role <span className="required">*</span>
                      </label>
                    </div>
                  </div>

                  <div className="modal-actions">
                    <button
                      type="button"
                      className="btn btn--outline"
                      onClick={() => {
                        setShowEditModal(false);
                        resetEditForm();
                      }}
                      disabled={editLoading}
                    >
                      Close
                    </button>
                    <button
                      type="submit"
                      className="btn btn--primary"
                      disabled={editLoading}
                    >
                      {editLoading ? (
                        <>
                          <span className="spinner spinner--sm"></span>
                          Updating...
                        </>
                      ) : (
                        "Update User"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Signature Upload Modal */}
          {showSignatureModal && user && (
            <div className="modal-overlay modal-overlay--center">
              <div className="modal-content modal-content--sm">
                <div className="modal-header">
                  <h3 className="modal-title">
                    {user.signatureExists
                      ? "Update Signature"
                      : "Add Signature"}
                  </h3>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={() => {
                      setShowSignatureModal(false);
                      resetSignatureForm();
                    }}
                    disabled={signatureLoading}
                  >
                    ✕
                  </button>
                </div>

                <div className="modal-body" style={{ padding: "1.5rem" }}>
                  {signatureError && (
                    <div
                      className="alert alert-error"
                      style={{ marginBottom: "1.5rem" }}
                    >
                      <span className="alert-icon">⚠️</span>
                      <span>{signatureError}</span>
                      <button
                        type="button"
                        onClick={() => setSignatureError("")}
                        className="alert-close"
                      >
                        ✕
                      </button>
                    </div>
                  )}

                  <div style={{ marginBottom: "0" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "0.75rem",
                        fontSize: "0.9rem",
                        fontWeight: "600",
                        color: "#333",
                      }}
                    >
                      Signature Image
                    </label>
                    <div
                      className="photo-upload-container"
                      style={{ marginBottom: "0.75rem" }}
                    >
                      {signaturePreview ? (
                        <div className="photo-preview">
                          <img
                            src={getFullPath(signaturePreview) || undefined}
                            alt="Signature Preview"
                            className="preview-image"
                            style={{ background: "#fff" }}
                          />
                          <button
                            type="button"
                            className="btn btn--sm btn--danger"
                            onClick={removeSignature}
                            disabled={signatureLoading}
                          >
                            Remove Signature
                          </button>
                        </div>
                      ) : (
                        <div className="photo-upload-area">
                          <input
                            type="file"
                            id="signatureUpload"
                            accept="image/*"
                            onChange={handleSignatureChange}
                            style={{ display: "none" }}
                            disabled={signatureLoading}
                          />
                          <label
                            htmlFor="signatureUpload"
                            className="photo-upload-label"
                          >
                            <div className="upload-icon">✍️</div>
                            <div className="upload-text">
                              <span className="upload-title">
                                Click to upload signature
                              </span>
                              <span className="upload-subtitle">
                                PNG, JPG up to 5MB
                              </span>
                            </div>
                          </label>
                        </div>
                      )}
                    </div>
                    <p
                      className="form-help"
                      style={{
                        marginTop: "0",
                        marginBottom: "0",
                        fontSize: "0.85rem",
                        color: "#666",
                        lineHeight: "1.5",
                      }}
                    >
                      Upload an image of the user's signature. This can be used
                      on official documents.
                    </p>
                  </div>
                </div>

                <div
                  className="modal-actions"
                  style={{
                    padding: "1rem 1.5rem",
                    borderTop: "1px solid #e0e0e0",
                  }}
                >
                  <button
                    type="button"
                    className="btn btn--outline"
                    onClick={() => {
                      setShowSignatureModal(false);
                      resetSignatureForm();
                    }}
                    disabled={signatureLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={handleUploadSignature}
                    disabled={signatureLoading || !signatureFile}
                  >
                    {signatureLoading ? (
                      <>
                        <span className="spinner spinner--sm"></span>
                        Uploading...
                      </>
                    ) : user.signatureExists ? (
                      "Update Signature"
                    ) : (
                      "Upload Signature"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default UserDetail;
